<script lang="ts" setup>
import { computed, ref } from "vue";
import type { TransactionMessage } from "@/types";
import type { BodySource } from "@/queries/runs";
import { statusCodeBadge } from "../utils/transactionFormatters";
import TransactionField from "./TransactionField.vue";
import TransactionBody from "./TransactionBody.vue";

// One side of an HTTP exchange as labelled fields. The parent supplies the `<dl class="row mb-0 small">`.
const props = defineProps<{
  kind: "request" | "response";
  message: TransactionMessage;
  runId: string;
  serviceId: string;
  transactionId: string;
  /** Which attempt's body to read. Absent means the transaction's own result. */
  attemptId?: string;
}>();

const headersOpen = ref(false);

const bodySource = computed<BodySource>(() => {
  if (props.attemptId === undefined) return "result";
  return { attemptId: props.attemptId, kind: props.kind };
});
</script>

<template>
  <template v-if="kind === 'request'">
    <TransactionField label="Method">{{ message.method }}</TransactionField>
    <TransactionField label="URL"><span class="font-monospace">{{ message.url }}</span></TransactionField>
  </template>
  <TransactionField v-else-if="message.statusCode != null" label="Status">
    <span class="badge" :class="statusCodeBadge(message.statusCode)">
      {{ message.statusCode }} {{ message.reasonPhrase }}
    </span>
  </TransactionField>

  <TransactionField label="Headers">
    <span v-if="!message.headers.length" class="text-body-secondary">None</span>
    <template v-else>
      <button
        type="button"
        class="btn btn-link btn-sm p-0 align-baseline"
        :aria-expanded="headersOpen"
        @click="headersOpen = !headersOpen"
      >
        {{ headersOpen ? "Hide" : "Show" }}
      </button>
      <table v-if="headersOpen" class="table table-sm table-borderless mt-1 mb-0">
        <tbody>
          <tr v-for="header in message.headers" :key="header.name">
            <td class="text-body-secondary text-nowrap ps-0 pe-3">{{ header.name }}</td>
            <td class="font-monospace px-0">{{ header.value }}</td>
          </tr>
        </tbody>
      </table>
    </template>
  </TransactionField>

  <TransactionField label="Body">
    <TransactionBody
      :body="message.body"
      :source="bodySource"
      :run-id="runId"
      :service-id="serviceId"
      :transaction-id="transactionId"
    />
  </TransactionField>
</template>
